/**
 * Drag-to-dismiss for the cart drawer.
 *
 * The drawer already animates open/close via the theme's `move-and-fade`
 * keyframe, driven by the `--start-x` / `--end-x` custom properties
 * (see assets/base.css). This adds 1:1 pointer tracking on the drawer
 * header, so it can be flicked shut like a native sheet, then hands off
 * to the theme's own `closeDialog()` for the actual close — starting the
 * closing animation from wherever the drag left off (not a fixed 0px),
 * so there's no jump between "being dragged" and "closing".
 *
 * Cancelling a drag (not dragged far/fast enough) snaps back via a
 * plain `!important`-tagged inline transition, which sits above the
 * CSS Animation in the cascade without disturbing it — so removing the
 * override afterwards hands control back to the (unmodified, still
 * "open" and forwards-filled) animation with no visible seam either way.
 */
(function () {
  'use strict';

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var DRAG_HYSTERESIS = 10; // px, before committing to a horizontal drag
  var CLOSE_DISTANCE_RATIO = 0.35; // fraction of drawer width to auto-close
  var CLOSE_VELOCITY = 0.5; // px/ms release speed that auto-closes regardless of distance
  var RUBBERBAND_CONSTANT = 0.55;

  function rubberband(overshoot, dimension) {
    var sign = overshoot < 0 ? -1 : 1;
    var magnitude = Math.abs(overshoot);
    return sign * (magnitude * dimension * RUBBERBAND_CONSTANT) / (dimension + RUBBERBAND_CONSTANT * magnitude);
  }

  function currentTranslateX(el) {
    var transform = getComputedStyle(el).transform;
    if (!transform || transform === 'none') return 0;
    try {
      return new DOMMatrixReadOnly(transform).m41;
    } catch (e) {
      return 0;
    }
  }

  function init() {
    var cartDrawer = document.querySelector('cart-drawer-component');
    if (!cartDrawer || typeof cartDrawer.closeDialog !== 'function') return;

    var dialog = cartDrawer.querySelector('dialog.cart-drawer__dialog');
    var header = cartDrawer.querySelector('.cart-drawer__header');
    if (!dialog || !header) return;

    header.style.touchAction = 'pan-y';

    var pointerId = null;
    var dragging = false;
    var committed = false;
    var startX = 0;
    var startY = 0;
    var baseX = 0;
    var currentX = 0;
    var width = 0;
    var history = [];

    function onPointerDown(event) {
      if (!dialog.open || dialog.classList.contains('dialog-closing')) return;
      if (event.target.closest('button, a, input, select, textarea, [role="button"]')) return;

      pointerId = event.pointerId;
      dragging = true;
      committed = false;
      startX = event.clientX;
      startY = event.clientY;
      baseX = currentTranslateX(dialog);
      currentX = baseX;
      width = dialog.getBoundingClientRect().width || 1;
      history = [{ x: event.clientX, t: event.timeStamp }];
    }

    function onPointerMove(event) {
      if (!dragging || event.pointerId !== pointerId) return;

      var dx = event.clientX - startX;
      var dy = event.clientY - startY;

      if (!committed) {
        if (Math.abs(dx) < DRAG_HYSTERESIS && Math.abs(dy) < DRAG_HYSTERESIS) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          dragging = false; // vertical intent — let the page/header alone
          return;
        }
        committed = true;
        header.setPointerCapture(pointerId);
        header.style.cursor = 'grabbing';
      }

      event.preventDefault();

      var raw = baseX + dx;
      currentX = raw >= 0 ? raw : rubberband(raw, width);

      history.push({ x: event.clientX, t: event.timeStamp });
      if (history.length > 5) history.shift();

      dialog.style.setProperty('transform', 'translateX(' + currentX + 'px)', 'important');
    }

    function onPointerUp(event) {
      if (!dragging || event.pointerId !== pointerId) return;
      dragging = false;
      header.style.cursor = '';
      if (!committed) return;

      try {
        header.releasePointerCapture(pointerId);
      } catch (err) {
        /* already released */
      }

      var velocity = 0;
      if (history.length >= 2) {
        var first = history[0];
        var last = history[history.length - 1];
        var dt = last.t - first.t;
        if (dt > 0) velocity = (last.x - first.x) / dt;
      }

      var shouldClose = currentX > width * CLOSE_DISTANCE_RATIO || velocity > CLOSE_VELOCITY;

      if (shouldClose) {
        dialog.style.setProperty('--start-x', currentX + 'px', 'important');
        dialog.style.removeProperty('transform');

        var onClose = function () {
          dialog.style.removeProperty('--start-x');
          cartDrawer.removeEventListener('dialog:close', onClose);
        };
        cartDrawer.addEventListener('dialog:close', onClose);
        cartDrawer.closeDialog();
      } else {
        dialog.style.transition = 'transform 0.32s cubic-bezier(0.32, 0.72, 0, 1)';
        dialog.style.setProperty('transform', 'translateX(' + baseX + 'px)', 'important');

        var onSnapBackDone = function (transitionEvent) {
          if (transitionEvent.propertyName !== 'transform') return;
          dialog.style.transition = '';
          dialog.style.removeProperty('transform');
          dialog.removeEventListener('transitionend', onSnapBackDone);
        };
        dialog.addEventListener('transitionend', onSnapBackDone);
      }
    }

    header.style.cursor = 'grab';
    header.addEventListener('pointerdown', onPointerDown);
    header.addEventListener('pointermove', onPointerMove);
    header.addEventListener('pointerup', onPointerUp);
    header.addEventListener('pointercancel', onPointerUp);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
